import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { retryTransient } from '../utils/http-retry.utils';
import {
  LookupOption,
  LookupType,
  LookupTypeCode,
  LookupValue,
} from '../models/lookup.models';

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/lookups`;

  // Cache for lookup types - these rarely change, so cache aggressively
  private lookupTypeCache = new Map<string, Observable<LookupType>>();

  /**
   * Get all lookup types
   */
  getAllLookupTypes(): Observable<LookupType[]> {
    return this.http
      .get<LookupType[]>(`${this.apiUrl}/types`)
      .pipe(retryTransient(1));
  }

  /**
   * Get a lookup type by its code (includes values)
   * Results are cached to avoid repeated API calls
   */
  getLookupTypeByCode(typeCode: LookupTypeCode | string): Observable<LookupType> {
    // Check cache first
    if (!this.lookupTypeCache.has(typeCode)) {
      const request = this.http
        .get<LookupType>(`${this.apiUrl}/types/code/${typeCode}`)
        .pipe(
          retryTransient(1),
          shareReplay(1), // Cache the result
        );
      this.lookupTypeCache.set(typeCode, request);
    }
    return this.lookupTypeCache.get(typeCode)!;
  }

  /**
   * Get lookup values for a specific type code
   */
  getLookupValues(typeCode: LookupTypeCode | string): Observable<LookupValue[]> {
    return this.getLookupTypeByCode(typeCode).pipe(
      map((lookupType) => lookupType.values || [])
    );
  }

  /**
   * Get lookup values as options for dropdowns/filters
   * Maps code -> value, name -> label
   */
  getLookupOptions(typeCode: LookupTypeCode | string): Observable<LookupOption[]> {
    return this.getLookupValues(typeCode).pipe(
      map((values) =>
        values
          .filter((v) => v.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((v) => ({
            value: v.code,
            label: v.name,
          }))
      )
    );
  }

  /**
   * Clear the cache for a specific type code or all caches
   */
  clearCache(typeCode?: string): void {
    if (typeCode) {
      this.lookupTypeCache.delete(typeCode);
    } else {
      this.lookupTypeCache.clear();
    }
  }
}
